import React from "react";
import {
  TextField,
  CircularProgress,
  Alert,
  Box,
  Autocomplete,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { useDepartments } from "../hooks";
import type { Department } from "../api/types";

interface DepartmentSelectProps {
  value: string[];
  onChange: (values: string[]) => void;
  label?: string;
  regionIds?: string[];
}

export const DepartmentSelect: React.FC<DepartmentSelectProps> = ({
  value,
  onChange,
  label = "Select Departments",
  regionIds,
}) => {
  const { data, isLoading, error } = useDepartments({ regionIds });

  if (error) {
    return (
      <Alert severity="error">
        Failed to load departments. Please try again.
      </Alert>
    );
  }

  const options = data?.data || [];
  const selectedDepartments = options.filter((dept) =>
    value.includes(dept.id)
  );

  const handleChange = (_event: unknown, newValue: Department[]) => {
    onChange(newValue.map((dept) => dept.id));
  };

  return (
    <Box>
      <Autocomplete
        multiple
        fullWidth
        options={options}
        value={selectedDepartments}
        onChange={handleChange}
        getOptionLabel={(option) => option.name}
        loading={isLoading}
        filterSelectedOptions
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search departments..."
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};
